import React from 'react';
import {StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {Card} from '@/shared/ui/card';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Раскладка форм — три примитива, из которых собирается любая форма кабинета.
 *
 * ## Зачем
 *
 * На мониторе форма сотрудника была телефоном, растянутым до 820pt: восемь полей
 * столбиком, каждое во всю ширину, между ними по 16pt воздуха, и «Сохранить» —
 * где-то через три экрана прокрутки. Поле для табельного номера в 820pt шириной —
 * это поле, в котором строка из шести знаков висит в левом углу пустого прямоугольника.
 *
 * shadcn кладёт короткие поля парами (`grid grid-cols-2 gap-4`), режет форму на
 * карточки с заголовком и описанием и на широком окне ставит второстепенное — фото,
 * статус, срок — колонкой сбоку от главного. Здесь то же самое:
 *
 *      ┌ FormLayout ─────────────────────────────────────────────────────────┐
 *      │ main                                          │ aside                │
 *      │ ┌ FormCard «Основное» ────────────────────┐   │ ┌ FormCard «Фото» ─┐ │
 *      │ │ Имя и фамилия     [__________________]  │   │ │  (o)  [Снять]    │ │
 *      │ │ FormRow: Должность [_____] Табель [___] │   │ └──────────────────┘ │
 *      │ └─────────────────────────────────────────┘   │ ┌ FormCard «Срок» ─┐ │
 *      │ ┌ FormCard «Отделы» ──────────────────────┐   │ │ Ограничить  (o)  │ │
 *      └─────────────────────────────────────────────────────────────────────┘
 *
 * На телефоне всё это — одна колонка в прежнем порядке: `FormRow` складывает пару
 * в столбик, `FormLayout` ставит `aside` под `main`. Экран пишет раскладку один раз.
 */

const styles = StyleSheet.create({
  layout: {gap: spacing(5)},
  layoutWide: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(6)},
  main: {flex: 1, minWidth: 0, gap: spacing(5)},
  aside: {gap: spacing(5)},
  asideWide: {width: 320, flexShrink: 0},

  row: {gap: spacing(4)},
  rowWide: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(4)},
  cell: {flex: 1, minWidth: 0},

  card: {gap: spacing(4)},
  cardWide: {paddingHorizontal: spacing(5), paddingVertical: spacing(5)},
  head: {gap: 2, marginBottom: spacing(1)},
  headRow: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(3)},
  headText: {flex: 1, minWidth: 0, gap: 2},
});

/**
 * Главное и сбоку. На широком окне — две колонки (сбоку 320pt), на телефоне —
 * столбик: сначала `main`, потом `aside`.
 */
export function FormLayout({
  main,
  aside,
  style,
  testID,
}: {
  main: React.ReactNode;
  aside?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const {isDesktop} = useBreakpoint();
  if (!aside) {
    return (
      <View testID={testID} style={[styles.main, style]}>
        {main}
      </View>
    );
  }
  return (
    <View testID={testID} style={[isDesktop ? styles.layoutWide : styles.layout, style]}>
      <View style={styles.main}>{main}</View>
      <View style={[styles.aside, isDesktop && styles.asideWide]}>{aside}</View>
    </View>
  );
}

/**
 * Пара (или тройка) коротких полей в одной строке — shadcn `grid-cols-2 gap-4`.
 * На телефоне складывается в столбик. `null`-дети пропускаются, так что условное
 * поле не оставляет пустой ячейки.
 */
export function FormRow({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const {isDesktop} = useBreakpoint();
  const cells = React.Children.toArray(children).filter(Boolean);
  if (!isDesktop) {
    return <View style={[styles.row, style]}>{cells}</View>;
  }
  return (
    <View style={[styles.rowWide, style]}>
      {cells.map((cell, i) => (
        <View key={i} style={styles.cell}>
          {cell}
        </View>
      ))}
    </View>
  );
}

/**
 * Карточка-секция формы — shadcn `Card` с `CardHeader` (имя, описание) и
 * `CardContent` (поля). Имя и описание живут ВНУТРИ карточки: заголовок, висящий над
 * рамкой, читается как заголовок страницы, а страница уже названа.
 */
export function FormCard({
  title,
  hint,
  aside,
  children,
  style,
  testID,
}: {
  title?: string;
  /** Тихая строка под именем — зачем этот блок и что в нём. */
  hint?: string | null;
  /** Справа от имени — переключатель, значок «?». */
  aside?: React.ReactNode;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const {isDesktop} = useBreakpoint();
  const p = useTheme();
  return (
    <Card padding={4} style={[styles.card, isDesktop && styles.cardWide, style]} testID={testID}>
      {title || aside ? (
        <View style={[styles.head, aside ? styles.headRow : null]}>
          <View style={styles.headText}>
            {title ? <AppText variant="heading">{title}</AppText> : null}
            {hint ? (
              <AppText variant="caption" color={p.mutedForeground}>
                {hint}
              </AppText>
            ) : null}
          </View>
          {aside}
        </View>
      ) : null}
      {children}
    </Card>
  );
}
